import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import cidadesData from '../../Utils/cidades.json';
import "./ImoveisManagementPage.css";

const Modal = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          X
        </button>
        {children}
      </div>
    </div>
  );
};

const districts = Object.keys(cidadesData);

const ImoveisManagementPage = () => {
  const [imoveis, setImoveis] = useState([]);
  const [newImovel, setNewImovel] = useState({
    title: "",
    district: "",
    city: "",
    category: "",
    price: "",
    area: "",
    rooms: "",
    bathrooms: "",
    garage_slots: "",
    images_path: [],
    description: "",
    business_type: "",
    active: true,
    premium: false,
    opportunity: false,
    user_id: localStorage.getItem("currentUserId"),
    prompt_image: "",
  });
  const [editImovel, setEditImovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [language, setLanguage] = useState(localStorage.getItem("language") || "pt");
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);

  const categories = [
    "Apartamentos",
    "Quartos",
    "Moradias",
    "Terrenos",
    "Quintas e Herdades",
    "Garagens",
    "Prédios",
    "Escritórios",
    "Armazéns",
    "Lojas",
  ];

  const businessTypes = ["Comprar", "Arrendar"];
  const garageTypes = [
    { label: "Box", value: "Box" },
    { label: "Lugar", value: "Lugar" },
    { label: "Sem", value: "Sem" }
  ];

  const getApiUrl = () => {
    return "/api"; // Standardized API base path
  };

  const API_URL = getApiUrl(); // Use standardized API URL

  const userRole = localStorage.getItem("userRole");

  // Fetch all users for admin owner change
  useEffect(() => {
    if (userRole === "Admin") {
      axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then(res => setAllUsers(res.data))
      .catch(() => setAllUsers([]));
    }
  }, [userRole]);

  useEffect(() => {
    fetchImoveis();
  }, []);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguage(localStorage.getItem("language") || "pt");
    };
    window.addEventListener("languageChange", handleLanguageChange);
    return () => window.removeEventListener("languageChange", handleLanguageChange);
  }, []);

  const fetchImoveis = () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    axios
      .get(`${API_URL}/users/imoveis`, { // <-- always /users/imoveis
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setImoveis(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching imoveis:", error);
        setLoading(false);
      });
  };

  // Always update selectedFiles on file input change
  const handleImageUploadInput = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  // Upload images for a given imovelId, always use selectedFiles
  const handleImageUpload = async (imovelId) => {
    const formData = new FormData();
    const token = localStorage.getItem("authToken");
    if (selectedFiles.length > 0) {
      // Use images[] for multiple files
      selectedFiles.forEach(file => {
        formData.append("images[]", file);
      });
      formData.append("imovelId", imovelId);

      try {
        const res = await axios.post(
          `${API_URL}/users/imoveis/upload`, // <-- always /users/imoveis/upload
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSelectedFiles([]); // Clear after upload
        return res.data;
      } catch (error) {
        console.error("Error uploading images:", error);
      }
    }
  };

  // On create, always use selectedFiles for upload, then clear input and selectedFiles
  const handleCreateImovel = async () => {
    if (!validateInputs(newImovel)) return;

    const token = localStorage.getItem("authToken");

    const data = {
      ...newImovel,
      images_path: [],
      prompt_image: "",
    };

    try {
      const response = await axios.post(`${API_URL}/users/imoveis`, data, { // <-- always /users/imoveis
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const createdImovel = response.data;

      let uploadedFileNames = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await handleImageUpload(createdImovel.id);
        if (uploadResult && Array.isArray(uploadResult.uploaded)) {
          uploadedFileNames = uploadResult.uploaded;
        }
      }

      if (uploadedFileNames.length > 0) {
        await axios.put(
          `${API_URL}/users/imoveis/${createdImovel.id}`, // <-- always /users/imoveis/{id}
          {
            ...newImovel,
            id: createdImovel.id,
            images_path: uploadedFileNames,
            prompt_image: uploadedFileNames[0],
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      fetchImoveis();

      setNewImovel({
        title: "",
        district: "",
        city: "",
        category: "",
        price:"",
        area: "",
        rooms: "",
        bathrooms: "",
        garage_slots: "",
        images_path: [],
        prompt_image: "",
        description: "",
        business_type: "",
        active: true,
        premium: false,
        opportunity: false,
        user_id: localStorage.getItem("currentUserId"),
      });
      setIsModalOpen(false);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      console.error("Error creating imovel:", error);
    }
  };

  // On edit, always use selectedFiles for upload, then clear input and selectedFiles
  const handleUpdateImovel = async () => {
    const userRole = localStorage.getItem("userRole");
    const currentUserId = localStorage.getItem("currentUserId");
    if (userRole !== "Admin" && editImovel.user_id != currentUserId) {
      alert("Não tem permissões para editar este Imóvel.");
      return;
    }

    if (!validateInputs(editImovel)) return;

    const token = localStorage.getItem("authToken");

    let uploadedFileNames = [];
    if (selectedFiles.length > 0) {
      const uploadResult = await handleImageUpload(editImovel.id);
      if (uploadResult && Array.isArray(uploadResult.uploaded)) {
        uploadedFileNames = uploadResult.uploaded;
      }
    }

    const mergedImagesPath = [
      ...(editImovel.images_path || []),
      ...uploadedFileNames.filter(f => !(editImovel.images_path || []).includes(f))
    ];

    const data = {
      ...editImovel,
      images_path: mergedImagesPath,
      prompt_image: editImovel.prompt_image || mergedImagesPath[0] || "",
      id: editImovel.id,
    };

    try {
      await axios.put(
        `${API_URL}/users/imoveis/${editImovel.id}`, // <-- always /users/imoveis/{id}
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchImoveis();

      setEditImovel(null);
      setIsModalOpen(false);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error updating imovel:", error);
    }
  };

  // When opening modal for edit, set district/city/cities state immediately
  const handleEditImovel = (imovel) => {
    let images_path = imovel.images_path;
    if (typeof images_path === "string") {
      try {
        images_path = JSON.parse(images_path);
      } catch {
        images_path = [];
      }
    }
    setEditImovel({
      ...imovel,
      images_path: Array.isArray(images_path) ? images_path : [],
      prompt_image: imovel.prompt_image || (Array.isArray(images_path) && images_path[0]) || "",
    });
    setDistrict(imovel.district || '');
    setCities(imovel.district ? (cidadesData[imovel.district] || []) : []);
    setCity(imovel.city || '');
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSelectedFiles([]);
    setIsModalOpen(true);
  };

  // When opening modal for create, set cities/city immediately
  const handleOpenCreateModal = () => {
    setNewImovel({
      title: "",
      district: "",
      city: "",
      category: "",
      price:"",
      area: "",
      rooms: "",
      bathrooms: "",
      garage_slots: "",
      images_path: [],
      description: "",
      business_type: "",
      active: true,
      premium: false,
      opportunity: false,
      user_id: localStorage.getItem("currentUserId"),
    });
    setEditImovel(null);
    setDistrict('');
    setCity('');
    setCities([]);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (imovel) => {
    const token = localStorage.getItem("authToken");
    try {
      await axios.put(
        `${API_URL}/users/imoveis/${imovel.id}/toggle-active`, // <-- always /users/imoveis/{id}/toggle-active
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchImoveis();
    } catch (error) {
      console.error("Error toggling imovel status:", error);
    }
  };

  // Remove an image from the images_path array for the imovel being edited
  const handleDeleteImage = (index) => {
    if (!editImovel) return;
    const updatedImages = [...editImovel.images_path];
    const removedImage = updatedImages.splice(index, 1)[0];
    let newPromptImage = editImovel.prompt_image;
    // If the removed image was the prompt_image, pick another or empty
    if (removedImage === editImovel.prompt_image) {
      newPromptImage = updatedImages[0] || "";
    }
    setEditImovel({
      ...editImovel,
      images_path: updatedImages,
      prompt_image: newPromptImage,
    });
  };

  // Set the prompt_image for the imovel being edited
  const handleSetPromptImage = (image) => {
    if (!editImovel) return;
    setEditImovel({
      ...editImovel,
      prompt_image: image,
    });
  };

  // Delete an imovel by id (with permission check)
  const handleDeleteImovel = async (id, imovelUserId) => {
    const userRole = localStorage.getItem("userRole");
    const currentUserId = localStorage.getItem("currentUserId");
    const token = localStorage.getItem("authToken");

    if (userRole !== "Admin" && imovelUserId != currentUserId) {
      alert("Não tem permissões para apagar este Imóvel.");
      return;
    }

    if (!window.confirm("Tem a certeza que deseja apagar este imóvel?")) return;

    try {
      await axios.delete(`${API_URL}/users/imoveis/${id}`, { // <-- always /users/imoveis/{id}
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchImoveis();
    } catch (error) {
      console.error("Erro ao apagar imóvel:", error);
      alert("Erro ao apagar imóvel.");
    }
  };

  const validateInputs = (imovel) => {
    // Only check for empty fields, and price/area must be > 0, rooms/bathrooms can be 0
    if (
      !imovel.title ||
      !imovel.district ||
      !imovel.city ||
      !imovel.category ||
      imovel.price === "" ||
      imovel.area === "" ||
      imovel.rooms === "" ||
      imovel.bathrooms === "" ||
      !imovel.garage_slots ||
      !imovel.description ||
      !imovel.business_type
    ) {
      alert("Todos os campos são necessários.");
      return false;
    }

    if (isNaN(imovel.price) || Number(imovel.price) <= 0) {
      alert("Preço tem de ser um valor positivo.");
      return false;
    }

    if (isNaN(imovel.area) || Number(imovel.area) <= 0) {
      alert("Área tem de ser um valor positivo.");
      return false;
    }

    if (isNaN(imovel.rooms) || Number(imovel.rooms) < 0) {
      alert("Número de quartos tem de ser zero ou positivo.");
      return false;
    }

    if (isNaN(imovel.bathrooms) || Number(imovel.bathrooms) < 0) {
      alert("Número de casas de banho tem de ser zero ou positivo.");
      return false;
    }

    return true;
  };

  return (
    <div className="imoveis-management">
      <h1>Gestão de Imóveis</h1>
      <div className="imovel-form">
        <button
          className="create-imovel-button"
          onClick={handleOpenCreateModal}
        >
          Criar Imóvel
        </button>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2>{editImovel ? "Editar Imóvel" : "Criar Imóvel"}</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: "1 1 250px", minWidth: 200 }}>
            <input
              type="text"
              placeholder="Título"
              value={editImovel ? editImovel.title : newImovel.title}
              onChange={e => {
                const value = e.target.value;
                editImovel
                  ? setEditImovel({ ...editImovel, title: value })
                  : setNewImovel({ ...newImovel, title: value });
              }}
            />
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
            <select
              value={district}
              onChange={e => {
                const value = e.target.value;
                setDistrict(value);
                setCities(value ? (cidadesData[value] || []) : []);
                setCity('');
                if (editImovel) {
                  setEditImovel(imovel => imovel ? { ...imovel, district: value, city: '' } : imovel);
                } else {
                  setNewImovel(nm => ({ ...nm, district: value, city: '' }));
                }
              }}
            >
              <option value="">Selecionar Distrito</option>
              {districts.map((d, idx) => (
                <option key={idx} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
            <select
              value={city}
              onChange={e => {
                const value = e.target.value;
                setCity(value);
                if (editImovel) {
                  setEditImovel(imovel => imovel ? { ...imovel, city: value } : imovel);
                } else {
                  setNewImovel(nm => ({ ...nm, city: value }));
                }
              }}
              disabled={!district}
            >
              <option value="">Selecionar Cidade</option>
              {cities.map((c, idx) => (
                <option key={idx} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
            <select
              value={editImovel ? editImovel.category : newImovel.category}
              onChange={e => {
                const value = e.target.value;
                editImovel
                  ? setEditImovel({ ...editImovel, category: value })
                  : setNewImovel({ ...newImovel, category: value });
              }}
            >
              <option value="">Selecionar Categoria</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 120px", minWidth: 100 }}>
            <select
              value={editImovel ? editImovel.business_type : newImovel.business_type}
              onChange={e => {
                const value = e.target.value;
                editImovel
                  ? setEditImovel({ ...editImovel, business_type: value })
                  : setNewImovel({ ...newImovel, business_type: value });
              }}
            >
              <option value="">Tipo de Negócio</option>
              {businessTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10 }}>
          <input
            type="text"
            placeholder="Preço"
            value={editImovel ? editImovel.price : newImovel.price}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, price: value })
                : setNewImovel({ ...newImovel, price: value });
            }}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          />
          <input
            type="text"
            placeholder="Área"
            value={editImovel ? editImovel.area : newImovel.area}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, area: value })
                : setNewImovel({ ...newImovel, area: value });
            }}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          />
          <input
            type="text"
            placeholder="Quartos"
            value={editImovel ? editImovel.rooms : newImovel.rooms}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, rooms: value })
                : setNewImovel({ ...newImovel, rooms: value });
            }}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          />
          <input
            type="text"
            placeholder="Casas de Banho"
            value={editImovel ? editImovel.bathrooms : newImovel.bathrooms}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, bathrooms: value })
                : setNewImovel({ ...newImovel, bathrooms: value });
            }}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          />
          <select
            value={editImovel ? editImovel.garage_slots : newImovel.garage_slots}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, garage_slots: value })
                : setNewImovel({ ...newImovel, garage_slots: value });
            }}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          >
            <option value="">Tipo de Garagem</option>
            {garageTypes.map((type, index) => (
              <option key={index} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <input
          type="file"
          multiple
          onChange={handleImageUploadInput}
          aria-label="Selecionar fotografias"
          ref={fileInputRef}
          style={{ marginTop: 10 }}
        />
        {editImovel && Array.isArray(editImovel.images_path) && editImovel.images_path.length > 0 ? (
          <div className="existing-images">
            <h3>Imagens existentes</h3>
            <table>
              <thead>
                <tr>
                  <th>Imagens</th>
                  <th>Acções</th>
                </tr>
              </thead>
              <tbody>
                {editImovel.images_path.map((image, index) => (
                  <tr key={index}>
                    <td>
                      <img
                        src={`/Photos/Imoveis/${editImovel.id}/${image}`}
                        alt={`Preview ${image}`}
                        style={{ width: "100px", height: "auto" }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </td>
                    <td>
                      <button onClick={() => handleDeleteImage(index)}>
                        Apagar
                      </button>
                      <button
                        style={{
                          marginLeft: 8,
                          background: editImovel.prompt_image === image ? "#FFD700" : undefined,
                          color: editImovel.prompt_image === image ? "#333" : undefined,
                        }}
                        onClick={() => handleSetPromptImage(image)}
                      >
                        Definir como imagem principal
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <strong>prompt_image:</strong> {editImovel.prompt_image}
            </div>
          </div>
        ) : (
          <p>Sem imagens disponíveis</p>
        )}
        <br />
        <textarea
          placeholder="Descrição"
          value={editImovel ? editImovel.description : newImovel.description}
          onChange={e => {
            const value = e.target.value;
            editImovel
              ? setEditImovel({ ...editImovel, description: value })
              : setNewImovel({ ...newImovel, description: value });
          }}
          rows={6}
          style={{ minHeight: 100, marginTop: 10 }}
        />
        <div className="form-actions">
          <label>
            Premium
            <input
              type="checkbox"
              checked={editImovel ? editImovel.premium : newImovel.premium}
              onChange={(e) => {
                const value = e.target.checked;
                editImovel
                  ? setEditImovel({ ...editImovel, premium: value })
                  : setNewImovel({ ...newImovel, premium: value });
              }}
            />
          </label>

          <label>
            Oportunidade
            <input
              type="checkbox"
              checked={editImovel ? editImovel.opportunity : newImovel.opportunity}
              onChange={(e) => {
                const value = e.target.checked;
                editImovel
                  ? setEditImovel({ ...editImovel, opportunity: value })
                  : setNewImovel({ ...newImovel, opportunity: value });
              }}
            />
          </label>
        </div>

        {/* Only show owner select for Admin */}
        {userRole === "Admin" && (
          <select
            value={editImovel ? editImovel.user_id : newImovel.user_id}
            onChange={e => {
              const value = e.target.value;
              editImovel
                ? setEditImovel({ ...editImovel, user_id: value })
                : setNewImovel({ ...newImovel, user_id: value });
            }}
          >
            <option value="">Selecionar Proprietário</option>
            {allUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.name || user.id}
              </option>
            ))}
          </select>
        )}

        <button onClick={editImovel ? handleUpdateImovel : handleCreateImovel}>
          {editImovel ? "Editar Imóvel" : "Criar Imóvel"}
        </button>
      </Modal>

      {loading ? (
        <div>Carregar...</div>
      ) : (
        <table className="imoveis-table">
          <thead>
            <tr>
              <th>Estado</th>
              <th>Titulo</th>
              <th>Distrito</th>
              <th>Cidade</th>
              <th>Categoria</th>
              <th>Tipo de Negócio</th>
              <th>Premium</th>
              <th>Oportunidade</th>
              <th>Acções</th>
            </tr>
          </thead>
          <tbody>
            {imoveis.map((imovel) => (
              <tr key={imovel.id}>
                <td>
                  <button
                    className={`status-button ${imovel.active ? "active" : "inactive"}`}
                    onClick={() => handleToggleActive(imovel)}
                  >
                    {imovel.active ? "Ativo" : "Inativo"}
                  </button>
                </td>
                <td>{imovel.title}</td>
                <td>{imovel.district}</td>
                <td>{imovel.city}</td>
                <td>{imovel.category}</td>
                <td>{imovel.business_type}</td>
                <td>{imovel.premium ? "Sim" : "Não"}</td>
                <td>{imovel.opportunity ? "Sim" : "Não"}</td>
                <td>
                  <button
                    onClick={() => handleEditImovel(imovel)}
                  >
                    Editar
                  </button>
                  <button onClick={() => handleDeleteImovel(imovel.id, imovel.user_id)}>
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ImoveisManagementPage;
